// 로그인 상태 관리 — 토큰 보관 + 내 정보(/auth/me) 캐시
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { authApi, type Me } from "./authApi.ts";
import { clearToken, getToken, setToken } from "./tokenStorage.ts";

interface AuthState {
  me: Me | null;
  loading: boolean;
  /** 토큰을 저장하고 내 정보를 불러온다 */
  applyToken: (accessToken: string) => Promise<Me>;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      setMe(await authApi.me());
    } catch {
      // 토큰 만료·무효 → 로그아웃 상태로 정리
      clearToken();
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const applyToken = useCallback(async (accessToken: string): Promise<Me> => {
    setToken(accessToken);
    const loaded = await authApi.me();
    setMe(loaded);
    setLoading(false);
    return loaded;
  }, []);

  const logout = useCallback(async () => {
    // 서버에 알려 **리프레시 토큰을 끊고 현재 액세스 토큰도 차단**한다(IMP-6c).
    // 실패해도 로컬은 정리한다 — 사용자가 나가겠다는데 화면에 붙잡아 둘 이유가 없다.
    try {
      await authApi.logout();
    } catch {
      /* 네트워크 실패 등 — 로컬 정리는 그대로 진행 */
    }
    clearToken();
    setMe(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthState>(
    () => ({ me, loading, applyToken, refresh, logout }),
    [me, loading, applyToken, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth는 AuthProvider 안에서만 사용할 수 있습니다.");
  return context;
}
